import React from 'react';
import VideoThumbnail from './VideoThumbnail';

interface MatchCardProps {
  match: {
    id: string;
    name: string;
    date: string;
    time: string;
    tournament: {
      id: string;
      name: string;
      logo: string;
    };
    homeTeam: {
      name: string;
      logo: string;
      score: number;
    };
    awayTeam: {
      name: string;
      logo: string;
      score: number;
    };
    homePenaltyScore?: number;
    awayPenaltyScore?: number;
    status: 'upcoming' | 'live' | 'finished';
    venue?: string;
    thumbnailUrl?: string;
    duration?: string;
    videoUrl?: string;
    videoCount?: number;
  };
  onMatchClick: (matchId: string) => void;
  onEditMatch: (e: React.MouseEvent, match: any) => void;
  onDeleteMatch: (e: React.MouseEvent, match: any) => void;
  onLinkVideo: (e: React.MouseEvent, match: any) => void;
  className?: string;
}

const MatchCard: React.FC<MatchCardProps> = ({
  match,
  onMatchClick,
  onEditMatch,
  onDeleteMatch,
  onLinkVideo,
  className = ''
}) => {
  const isLive = match.status === 'live';
  const isUpcoming = match.status === 'upcoming';

  const getStatusColor = () => {
    if (isLive) return 'text-red-600';
    if (isUpcoming) return 'text-blue-600';
    return 'text-green-600';
  };

  const getStatusText = () => {
    if (isLive) return 'Live';
    if (isUpcoming) return 'Upcoming';
    return 'Completed';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    // Handle cases where timeString might already have AM/PM
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <div 
      className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200 group ${className}`}
    >
      {/* Video Thumbnail */}
      <div className="relative">
        <VideoThumbnail
          src={match.thumbnailUrl}
          alt={`${match.homeTeam.name} vs ${match.awayTeam.name}`}
          duration={match.duration}
          isLive={isLive}
          onClick={() => onMatchClick(match.id)}
          fallbackIcon="⚽"
        />
        

        {/* Video Count Indicator */}
        {match.videoCount !== undefined && match.videoCount > 0 && (
          <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded-md text-xs font-medium">
            {match.videoCount > 1 ? `+${match.videoCount - 1} more` : '1 video'}
          </div>
        )}

        {/* Action Buttons - Show on hover (only video link for now) */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
          <button
            onClick={(e) => onLinkVideo(e, match)}
            className="p-2 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-colors duration-200"
            title={match.videoUrl ? "Video linked" : "Link video"}
          >
            {match.videoUrl ? (
              <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            ) : (
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            )}
          </button>
          {/* Edit and Delete buttons hidden for now */}
          {/* <button
            onClick={(e) => onEditMatch(e, match)}
            className="p-2 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-colors duration-200"
            title="Edit match"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={(e) => onDeleteMatch(e, match)}
            className="p-2 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-colors duration-200"
            title="Delete match"
          >
            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button> */}
        </div>
      </div>

      {/* Card Content */}
      <div className="p-4">
        {/* Match Title with Arrow */}
        <div className="flex items-center justify-between mb-2">
          <h3 
            className="font-semibold text-gray-900 text-sm line-clamp-1 flex-1 pr-2" 
            title={match.name || `${match.homeTeam.name} vs ${match.awayTeam.name}`}
          >
            {match.name || `${match.homeTeam.name} vs ${match.awayTeam.name}`}
          </h3>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMatchClick(match.id);
            }}
            className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title="View match details"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Teams and Score */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <img 
              src={match.homeTeam.logo} 
              alt={`${match.homeTeam.name} logo`}
              className="w-5 h-5 rounded object-cover flex-shrink-0"
              onError={(e) => {
                e.currentTarget.src = 'https://via.placeholder.com/20x20/cccccc/666666?text=🏟️';
              }}
            />
            <span className="text-sm font-medium text-gray-900 truncate">{match.homeTeam.name}</span>
          </div>
          
          <div className="flex items-center space-x-2 mx-3">
            {isUpcoming ? (
              <span className="text-gray-400 text-sm font-medium">vs</span>
            ) : (
              <div className="flex items-center space-x-2">
                {/* Home team score with penalty */}
                <div className="flex items-center space-x-1">
                  {/* Show penalty score if it exists */}
                  {match.homePenaltyScore !== undefined && match.awayPenaltyScore !== undefined && 
                   (match.homePenaltyScore > 0 || match.awayPenaltyScore > 0) && (
                    <span className="text-xs text-gray-500">({match.homePenaltyScore})</span>
                  )}
                  <span className="text-sm font-bold text-gray-900">{match.homeTeam.score}</span>
                </div>
                
                <span className="text-sm font-medium text-gray-500">-</span>
                
                {/* Away team score with penalty */}
                <div className="flex items-center space-x-1">
                  <span className="text-sm font-bold text-gray-900">{match.awayTeam.score}</span>
                  {/* Show penalty score if it exists */}
                  {match.homePenaltyScore !== undefined && match.awayPenaltyScore !== undefined && 
                   (match.homePenaltyScore > 0 || match.awayPenaltyScore > 0) && (
                    <span className="text-xs text-gray-500">({match.awayPenaltyScore})</span>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2 flex-1 min-w-0 justify-end">
            <span className="text-sm font-medium text-gray-900 truncate">{match.awayTeam.name}</span>
            <img 
              src={match.awayTeam.logo} 
              alt={`${match.awayTeam.name} logo`}
              className="w-5 h-5 rounded object-cover flex-shrink-0"
              onError={(e) => {
                e.currentTarget.src = 'https://via.placeholder.com/20x20/cccccc/666666?text=🏟️';
              }}
            />
          </div>
        </div>

        {/* Date, Time, and Status */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center space-x-2">
            <span>{formatDate(match.date)}</span>
            <span>•</span>
            <span>{formatTime(match.time)}</span>
          </div>
          <span className={`font-medium ${getStatusColor()}`}>
            {getStatusText()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MatchCard;
